import React, { ComponentType, ReactNode, MouseEvent } from 'react';
import { EventCategory } from './analytics';
import { safeTrackEvent, useAnalyticsTracking } from './analytics-utils';
import { Button, ButtonProps, buttonVariants } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { cn } from './utils';

// Define a more flexible event type for analytics
export type AnalyticsEvent = 
  | React.MouseEvent<HTMLElement> 
  | React.FormEvent<HTMLElement>
  | Event;

// Define navigation link interface
export interface NavigationLinkProps {
  to: string;
  children: ReactNode;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  [key: string]: any; // Allow other props
}

// Type for the TrackedLink props
interface TrackedLinkProps<T = any> {
  category: string;
  label: string;
  to: string;
  children: ReactNode;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  [key: string]: any;
}

/**
 * Create a navigation component with tracking capabilities
 * This is an adapter that will work with any navigation component
 * @param NavigationComponent - The navigation component (Link from react-router, Next.js Link, etc.)
 */
export function createTrackedLink<T extends { to: string; onClick?: (event: any) => void; [key: string]: any }>(
  NavigationComponent: ComponentType<T>
) {
  return function TrackedLink({ 
    category, 
    label, 
    to, 
    onClick, 
    children,
    ...rest 
  }: TrackedLinkProps) {
    const handleClick = (event: any) => {
      // Track the click
      safeTrackEvent({
        category,
        label,
        context: { path: to }
      });

      // Call the original onClick if provided
      if (onClick) {
        onClick(event);
      }
    };

    // Create a clean props object for the navigation component
    const linkProps = {
      ...rest,
      to,
      onClick: handleClick
    } as T;

    return (
      <NavigationComponent {...linkProps}>
        {children}
      </NavigationComponent>
    );
  };
}

/**
 * Generic function for creating analytics buttons
 */
function createAnalyticsButton(
  category: string,
  children: ReactNode,
  label: string,
  props: any = {}
) {
  const { to, variant, size, className, ...rest } = props;

  // If it's a link button
  if (to) {
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      safeTrackEvent({
        category,
        label,
        context: { path: to }
      });
      
      if (rest.onClick) {
        rest.onClick(e);
      }
    };

    return (
      <Link
        to={to}
        className={cn(
          buttonVariants({ variant, size }),
          className
        )}
        onClick={handleClick}
        {...rest}
      >
        {children}
      </Link>
    );
  }

  // Regular button with onClick handler
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    safeTrackEvent({
      category,
      label,
      context: rest.name ? { buttonName: rest.name } : undefined
    });
    
    if (rest.onClick) {
      rest.onClick(e);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      {...rest}
      onClick={handleClick}
    >
      {children}
    </Button>
  );
}

/**
 * Creates a button with standardized service analytics tracking
 */
export function createServiceButton(
  children: ReactNode,
  label: string,
  props: any = {}
) {
  return createAnalyticsButton(EventCategory.Service, children, label, props);
}

/**
 * Creates a button with standardized navigation analytics tracking
 */
export function createNavigationButton(
  children: ReactNode,
  label: string,
  props: any = {}
) {
  return createAnalyticsButton(EventCategory.Navigation, children, label, props);
}

/**
 * Creates a button with standardized engagement analytics tracking
 */
export function createEngagementButton(
  children: ReactNode,
  label: string,
  props: any = {}
) {
  return createAnalyticsButton(EventCategory.Engagement, children, label, props);
}

/**
 * Creates a button with standardized contact analytics tracking
 */
export function createContactButton(
  children: ReactNode,
  label: string,
  props: any = {}
) {
  return createAnalyticsButton(EventCategory.Contact, children, label, props);
}

/**
 * Hook factory for creating analytics tracking hooks with standardized categories
 */
export function useServiceAnalytics(label: string, context?: Record<string, any>) {
  return useAnalyticsTracking(EventCategory.Service, 'click', label, context);
}

export function useNavigationAnalytics(label: string, context?: Record<string, any>) {
  return useAnalyticsTracking(EventCategory.Navigation, 'click', label, context);
}

export function useEngagementAnalytics(label: string, context?: Record<string, any>) {
  return useAnalyticsTracking(EventCategory.Engagement, 'click', label, context);
}

export function useContactAnalytics(label: string, context?: Record<string, any>) {
  return useAnalyticsTracking(EventCategory.Contact, 'click', label, context);
}

/**
 * Click handler factory functions with enhanced type safety and context
 */
export function serviceClick(label: string, context?: Record<string, any>, originalHandler?: (event: AnalyticsEvent) => void) {
  return (event: AnalyticsEvent) => {
    // Track the event with context
    safeTrackEvent({
      category: EventCategory.Service,
      label,
      context
    });

    if (originalHandler) {
      originalHandler(event);
    }
  };
}

export function navigationClick(label: string, context?: Record<string, any>, originalHandler?: (event: AnalyticsEvent) => void) {
  return (event: AnalyticsEvent) => {
    // Track the event with context
    safeTrackEvent({
      category: EventCategory.Navigation,
      label,
      context
    });

    if (originalHandler) {
      originalHandler(event);
    }
  };
}

export function engagementClick(label: string, context?: Record<string, any>, originalHandler?: (event: AnalyticsEvent) => void) {
  return (event: AnalyticsEvent) => {
    // Track the event with context
    safeTrackEvent({
      category: EventCategory.Engagement,
      label,
      context
    });

    if (originalHandler) {
      originalHandler(event);
    }
  };
}

export function contactClick(label: string, context?: Record<string, any>, originalHandler?: (event: AnalyticsEvent) => void) {
  return (event: AnalyticsEvent) => {
    // Track the event with context
    safeTrackEvent({
      category: EventCategory.Contact,
      label,
      context
    });

    if (originalHandler) {
      originalHandler(event);
    }
  };
} 